import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Row, Col, Card, Form, Button, Table, Modal, Spinner, Badge, Tabs, Tab } from 'react-bootstrap';
import { ThemeContext } from '../../context/ThemeContext';
import { LoadingState } from '../../components/common/LoadingState';
import { useTranslation } from 'react-i18next';
import FaqApiManager from '../../api/services/FaqApiManager';
import FaqIconPicker from '../../components/common/FaqIconPicker';
import { getFaqIconMeta, DEFAULT_FAQ_ICON_KEY } from '../../constants/faqIcons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const normalizeTicketCounts = (raw, fallbackList = []) => {
  if (raw && typeof raw === 'object') {
    const pendiente = Number(raw.pendiente ?? raw.pendientes ?? 0);
    const en_proceso = Number(raw.en_proceso ?? 0);
    const resuelto = Number(raw.resuelto ?? raw.resueltos ?? 0);
    const total = Number(raw.total ?? (pendiente + en_proceso + resuelto));
    return { total, pendiente, en_proceso, resuelto };
  }
  const list = Array.isArray(fallbackList) ? fallbackList : [];
  const pendiente = list.filter(item => item.estatus === 'pendiente').length;
  const en_proceso = list.filter(item => item.estatus === 'en_proceso').length;
  const resuelto = list.filter(item => item.estatus === 'resuelto').length;
  return {
    total: list.length,
    pendiente,
    en_proceso,
    resuelto,
  };
};

const FaqManager = () => {
  const { t } = useTranslation();
  const { isDark } = useContext(ThemeContext) || {};

  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem('rutex_faq_active_tab') || 'faqs';
  });

  const handleSelectTab = (tabKey) => {
    const nextTab = tabKey || 'faqs';
    setActiveTab(nextTab);
    sessionStorage.setItem('rutex_faq_active_tab', nextTab);
  };

  const [supportPhone, setSupportPhone] = useState('+584141234567');
  const [savingPhone, setSavingPhone] = useState(false);

  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [faqCounts, setFaqCounts] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => {
    return sessionStorage.getItem('rutex_faq_status_filter') || '';
  });

  const handleChangeFaqStatusFilter = (nextStatus) => {
    setStatusFilter(nextStatus);
    sessionStorage.setItem('rutex_faq_status_filter', nextStatus);
  };

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentId, setCurrentId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const initialForm = {
    pregunta: '',
    respuesta: '',
    icono: DEFAULT_FAQ_ICON_KEY,
    is_active: true,
  };
  const [formData, setFormData] = useState(initialForm);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [togglingId, setTogglingId] = useState(null);

  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [updatingTicketId, setUpdatingTicketId] = useState(null);
  const [ticketSearch, setTicketSearch] = useState('');
  const [ticketStatusFilter, setTicketStatusFilter] = useState(() => {
    return sessionStorage.getItem('rutex_ticket_status_filter') || '';
  });

  const handleChangeTicketStatusFilter = (nextStatus) => {
    setTicketStatusFilter(nextStatus);
    sessionStorage.setItem('rutex_ticket_status_filter', nextStatus);
  };

  const [ticketCounts, setTicketCounts] = useState({
    total: 0,
    pendiente: 0,
    en_proceso: 0,
    resuelto: 0,
  });

  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketEditForm, setTicketEditForm] = useState({
    estatus: 'pendiente',
    notas_admin: '',
  });
  const [savingTicket, setSavingTicket] = useState(false);

  const [showDeleteTicketModal, setShowDeleteTicketModal] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [deletingTicket, setDeletingTicket] = useState(false);

  const fetchFaqs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter !== '') params.is_active = statusFilter;

      const res = await FaqApiManager.getFaqs(params);
      if (res.success) {
        const list = res.data || [];
        setFaqs(list);

        const total = Number(res.counts?.total ?? res.total ?? list.length);
        const active = Number(res.counts?.active ?? res.active_count ?? list.filter(f => f.is_active).length);
        const inactive = Number(res.counts?.inactive ?? res.inactive_count ?? Math.max(0, total - active));
        setFaqCounts({ total, active, inactive });

        if (res.support_phone) {
          setSupportPhone(res.support_phone);
        }
      }
    } catch (err) {
      toast.error(t('toast_error_load_faqs', 'Error al cargar las preguntas frecuentes'));
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, t]);

  const fetchSupportTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const params = {};
      if (ticketSearch.trim()) params.search = ticketSearch.trim();
      if (ticketStatusFilter !== '') params.estatus = ticketStatusFilter;

      const res = await FaqApiManager.getSupportTickets(params);
      if (res.success) {
        const list = res.data || [];
        setTickets(list);
        setTicketCounts(normalizeTicketCounts(res.counts || res.summary, list));
      }
    } catch (err) {
      toast.error('Error al cargar los mensajes de soporte');
    } finally {
      setLoadingTickets(false);
    }
  }, [ticketSearch, ticketStatusFilter]);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  useEffect(() => {
    fetchSupportTickets();
  }, [fetchSupportTickets]);

  useEffect(() => {
    const syncCoreSupportPhone = async () => {
      try {
        const res = await FaqApiManager.getCoreSettings();
        if (res?.data?.support_phone) {
          setSupportPhone(res.data.support_phone);
        }
      } catch (err) {
        // Fallback already handled by fetchFaqs
      }
    };
    syncCoreSupportPhone();
  }, []);

  const handleSaveSupportPhone = async (e) => {
    e.preventDefault();
    const cleaned = supportPhone.trim();
    if (!cleaned) {
      toast.warning('Ingresa un número de teléfono válido');
      return;
    }

    setSavingPhone(true);
    try {
      const res = await FaqApiManager.updateSupportPhone(cleaned);
      if (res.success) {
        if (res.data?.support_phone) {
          setSupportPhone(res.data.support_phone);
        } else {
          setSupportPhone(cleaned);
        }
        toast.success('Número de WhatsApp de soporte actualizado para la App Móvil');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al actualizar el teléfono de soporte';
      toast.error(msg === '060' ? 'Solo el rol SuperAdmin o Admin puede modificar la configuración global' : msg);
    } finally {
      setSavingPhone(false);
    }
  };

  const handleToggleStatus = async (faq) => {
    setTogglingId(faq.id);
    try {
      const res = await FaqApiManager.toggleFaqStatus(faq.id);
      if (res.success) {
        const newStatus = Boolean(res.data.is_active);
        setFaqCounts(prev => {
          const nextActive = newStatus ? prev.active + 1 : Math.max(0, prev.active - 1);
          const nextInactive = Math.max(0, prev.total - nextActive);
          return { ...prev, active: nextActive, inactive: nextInactive };
        });

        if (statusFilter !== '' && String(newStatus ? '1' : '0') !== String(statusFilter)) {
          setFaqs(prev => prev.filter(f => f.id !== faq.id));
        } else {
          setFaqs(prev => prev.map(f => f.id === faq.id ? { ...f, is_active: newStatus } : f));
        }

        if (selectedFaq && selectedFaq.id === faq.id) {
          setSelectedFaq(prev => ({ ...prev, is_active: newStatus }));
        }
        toast.success(newStatus ? 'Pregunta visible en app móvil' : 'Pregunta oculta de la app móvil');
      }
    } catch (err) {
      toast.error('Error al cambiar el estado de la pregunta');
    } finally {
      setTogglingId(null);
    }
  };

  const handleOpenDetails = (faq) => {
    setSelectedFaq(faq);
    setShowDetailsModal(true);
  };

  const handleOpenCreate = () => {
    setFormData({
      pregunta: '',
      respuesta: '',
      icono: DEFAULT_FAQ_ICON_KEY,
      is_active: true,
    });
    setFormErrors({});
    setModalMode('create');
    setCurrentId(null);
    setShowFormModal(true);
  };

  const handleTransitionToEdit = (faq) => {
    setShowDetailsModal(false);
    handleOpenEdit(faq);
  };

  const handleOpenEdit = (faq) => {
    setFormData({
      pregunta: faq.pregunta,
      respuesta: faq.respuesta,
      icono: faq.icono || DEFAULT_FAQ_ICON_KEY,
      is_active: Boolean(faq.is_active),
    });
    setFormErrors({});
    setModalMode('edit');
    setCurrentId(faq.id);
    setShowFormModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.pregunta.trim()) {
      errors.pregunta = 'La pregunta es requerida';
    }
    if (!formData.respuesta.trim()) {
      errors.respuesta = 'La respuesta es requerida';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveFaq = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    const payload = {
      pregunta: formData.pregunta.trim(),
      respuesta: formData.respuesta.trim(),
      categoria: 'General',
      icono: formData.icono || DEFAULT_FAQ_ICON_KEY,
      is_active: formData.is_active,
    };

    try {
      if (modalMode === 'create') {
        const res = await FaqApiManager.createFaq(payload);
        if (res.success) {
          toast.success('Pregunta frecuente creada exitosamente');
          setShowFormModal(false);
          fetchFaqs();
        }
      } else {
        const res = await FaqApiManager.updateFaq(currentId, payload);
        if (res.success) {
          toast.success('Pregunta frecuente actualizada exitosamente');
          setShowFormModal(false);
          fetchFaqs();
        }
      }
    } catch (err) {
      toast.error('Ocurrió un error al guardar la información');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = (faq) => {
    setFaqToDelete(faq);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!faqToDelete) return;
    setDeleting(true);
    try {
      const res = await FaqApiManager.deleteFaq(faqToDelete.id);
      if (res.success) {
        toast.success('Pregunta frecuente eliminada exitosamente');
        setShowDeleteModal(false);
        setFaqToDelete(null);
        if (selectedFaq && selectedFaq.id === faqToDelete.id) {
          setShowDetailsModal(false);
        }
        fetchFaqs();
      }
    } catch (err) {
      toast.error('Error al eliminar la pregunta frecuente');
    } finally {
      setDeleting(false);
    }
  };

  const applyTicketUpdateState = (updatedTicket, serverCounts) => {
    setTickets(prev => {
      if (ticketStatusFilter !== '' && updatedTicket.estatus !== ticketStatusFilter) {
        return prev.filter(tItem => tItem.id !== updatedTicket.id);
      }
      return prev.map(tItem => tItem.id === updatedTicket.id ? updatedTicket : tItem);
    });

    if (selectedTicket && selectedTicket.id === updatedTicket.id) {
      setSelectedTicket(updatedTicket);
    }

    if (serverCounts) {
      setTicketCounts(normalizeTicketCounts(serverCounts));
    } else {
      fetchSupportTickets();
    }
  };

  const handleQuickChangeTicketStatus = async (ticket, nextStatus) => {
    if (!ticket || ticket.estatus === nextStatus) return;

    setUpdatingTicketId(ticket.id);
    try {
      const res = await FaqApiManager.updateSupportTicket(ticket.id, {
        estatus: nextStatus,
        notas_admin: ticket.notas_admin ?? null,
      });
      if (res.success && res.data) {
        applyTicketUpdateState(res.data, res.counts || res.summary);
        toast.success('Estado del ticket actualizado');
      }
    } catch (err) {
      toast.error('Error al actualizar el estado del ticket');
    } finally {
      setUpdatingTicketId(null);
    }
  };

  const handleOpenTicketModal = (ticket) => {
    setSelectedTicket(ticket);
    setTicketEditForm({
      estatus: ticket.estatus || 'pendiente',
      notas_admin: ticket.notas_admin || '',
    });
    setShowTicketModal(true);
  };

  const handleSaveTicket = async (e) => {
    e.preventDefault();
    if (!selectedTicket) return;

    setSavingTicket(true);
    try {
      const res = await FaqApiManager.updateSupportTicket(selectedTicket.id, {
        estatus: ticketEditForm.estatus,
        notas_admin: ticketEditForm.notas_admin.trim() || null,
      });
      if (res.success && res.data) {
        applyTicketUpdateState(res.data, res.counts || res.summary);
        toast.success('Ticket de soporte actualizado correctamente');
        setShowTicketModal(false);
      }
    } catch (err) {
      toast.error('Error al actualizar el ticket de soporte');
    } finally {
      setSavingTicket(false);
    }
  };

  const handleOpenDeleteTicket = (ticket) => {
    setTicketToDelete(ticket);
    setShowDeleteTicketModal(true);
  };

  const handleConfirmDeleteTicket = async () => {
    if (!ticketToDelete) return;
    setDeletingTicket(true);
    try {
      const res = await FaqApiManager.deleteSupportTicket(ticketToDelete.id);
      if (res.success) {
        toast.success('Ticket de soporte eliminado');
        setShowDeleteTicketModal(false);
        const deletedId = ticketToDelete.id;
        setTicketToDelete(null);
        if (selectedTicket && selectedTicket.id === deletedId) {
          setShowTicketModal(false);
        }
        if (res.counts || res.summary) {
          setTickets(prev => prev.filter(item => item.id !== deletedId));
          setTicketCounts(normalizeTicketCounts(res.counts || res.summary));
        } else {
          fetchSupportTickets();
        }
      }
    } catch (err) {
      toast.error('Error al eliminar el ticket de soporte');
    } finally {
      setDeletingTicket(false);
    }
  };

  const getTicketStatusMeta = (estatus) => {
    if (estatus === 'resuelto') {
      return {
        bg: '#d1fae5',
        color: '#065f46',
        border: '#a7f3d0',
        icon: 'mdi-check-circle',
        label: 'Resuelto',
      };
    }
    if (estatus === 'en_proceso') {
      return {
        bg: '#dbeafe',
        color: '#1e40af',
        border: '#bfdbfe',
        icon: 'mdi-progress-clock',
        label: 'En Proceso',
      };
    }
    return {
      bg: '#fef3c7',
      color: '#92400e',
      border: '#fde68a',
      icon: 'mdi-clock-alert-outline',
      label: 'Pendiente',
    };
  };

  const renderTicketStatusBadge = (estatus) => {
    const meta = getTicketStatusMeta(estatus);
    return (
      <span
        className="badge rounded-pill px-3 py-1"
        style={{
          backgroundColor: meta.bg,
          color: meta.color,
          fontWeight: 600,
          fontSize: '0.78rem',
        }}
      >
        <i className={`mdi ${meta.icon} me-1`}></i>
        {meta.label}
      </span>
    );
  };

  const titleColor = isDark ? '#ffffff' : '#010028';
  const containerBg = isDark ? '#161e38' : '#ffffff';
  const containerBorder = isDark ? '1px solid #2a3759' : 'none';
  const cardBorderRadius = '12px';

  return (
    <div className="faq-manager-page">
      <ToastContainer position="top-right" autoClose={3000} theme={isDark ? 'dark' : 'light'} />

      <div className="page-header d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <h3 className="page-title mb-1" style={{ color: titleColor, fontWeight: 'bold' }}>
            <i className="mdi mdi-frequently-asked-questions me-2" style={{ color: '#183eeb' }}></i>
            {t('faq_page_title', 'Preguntas Frecuentes (FAQ) & Centro de Soporte')}
          </h3>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item"><a href="!#" onClick={e => e.preventDefault()}>{t('menu_dashboard', 'Dashboard')}</a></li>
              <li className="breadcrumb-item">{t('menu_staff_control', 'Personal y Control')}</li>
              <li className="breadcrumb-item active" aria-current="page">{t('menu_faqs', 'Centro de Ayuda y Soporte')}</li>
            </ol>
          </nav>
        </div>
        <div className="d-flex gap-2 mt-2 mt-sm-0">
          <Button
            variant="outline-primary"
            style={{ borderRadius: '10px', fontWeight: '500' }}
            className="d-flex align-items-center gap-1 shadow-sm"
            onClick={() => {
              fetchFaqs();
              fetchSupportTickets();
            }}
            disabled={loading || loadingTickets}
          >
            <i className={`mdi mdi-refresh ${(loading || loadingTickets) ? 'mdi-spin' : ''}`}></i>
            {t('btn_refresh', 'Actualizar')}
          </Button>
          {activeTab === 'faqs' && (
            <Button
              className="d-flex align-items-center gap-1 shadow-sm"
              style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px', fontWeight: '500' }}
              onClick={handleOpenCreate}
            >
              <i className="mdi mdi-plus-circle me-1"></i>
              {t('btn_new_faq', 'Nueva Pregunta')}
            </Button>
          )}
        </div>
      </div>

      {/* CONFIGURACIÓN RÁPIDA DE WHATSAPP DE SOPORTE EN APP MÓVIL */}
      <Card
        className="shadow-sm mb-4"
        style={{
          borderRadius: cardBorderRadius,
          backgroundColor: containerBg,
          border: containerBorder,
        }}
      >
        <Card.Body className="p-3 px-md-4">
          <Form onSubmit={handleSaveSupportPhone}>
            <Row className="g-3 align-items-center">
              <Col xs={12} lg={7}>
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0 shadow-xs"
                    style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '10px',
                      backgroundColor: isDark ? '#1e293b' : '#eef2ff',
                      color: '#183eeb',
                      fontSize: '1.4rem',
                    }}
                  >
                    <i className="mdi mdi-whatsapp"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-1" style={{ color: titleColor, fontSize: '0.96rem' }}>
                      Teléfono / WhatsApp de Contacto en la App Móvil
                    </h6>
                    <p className="text-muted small mb-0" style={{ lineHeight: '1.4' }}>
                      Este número se sincroniza automáticamente con el botón de <strong>Chat de Ayuda</strong> en la App Móvil sin necesidad de actualizar la aplicación.
                    </p>
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={5}>
                <div className="d-flex align-items-center gap-2">
                  <div className="master-setting-input-group flex-grow-1">
                    <div className="master-setting-addon-left" title="WhatsApp de Soporte">
                      <i className="mdi mdi-whatsapp" style={{ fontSize: '1.2rem' }}></i>
                    </div>
                    <input
                      type="text"
                      placeholder="+584141234567"
                      value={supportPhone}
                      onChange={(e) => setSupportPhone(e.target.value)}
                      className="master-setting-control"
                      style={{ colorScheme: isDark ? 'dark' : 'light' }}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={savingPhone}
                    className="d-flex align-items-center justify-content-center gap-1 flex-shrink-0 shadow-sm"
                    style={{
                      height: '42px',
                      padding: '0 18px',
                      backgroundColor: '#183eeb',
                      borderColor: '#183eeb',
                      color: '#ffffff',
                      borderRadius: '10px',
                      fontWeight: '600',
                      fontSize: '0.88rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {savingPhone ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        <span>Guardando...</span>
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-content-save-outline"></i>
                        <span>Guardar Número</span>
                      </>
                    )}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Tabs
        activeKey={activeTab}
        onSelect={handleSelectTab}
        className="minimal-underline-tabs mb-4"
      >
        <Tab
          eventKey="faqs"
          title={
            <span>
              <i className="mdi mdi-frequently-asked-questions me-1"></i>
              Preguntas Frecuentes (FAQ)
              <Badge bg="secondary" pill className="ms-2">
                {faqCounts.total}
              </Badge>
            </span>
          }
        >
          <Row className="mb-4 g-3 mt-1">
            <Col xs={12} sm={4}>
              <Card
                className="shadow-sm border-0 text-white"
                style={{
                  backgroundColor: isDark ? '#1e293b' : '#00024f',
                  borderRadius: cardBorderRadius,
                  cursor: 'pointer',
                  outline: statusFilter === '' ? '3px solid #183eeb' : 'none',
                  transform: statusFilter === '' ? 'scale(1.02)' : 'none',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => handleChangeFaqStatusFilter('')}
                title="Ver todas las preguntas"
              >
                <Card.Body className="d-flex align-items-center justify-content-between p-3">
                  <div>
                    <div className="text-white-50 small fw-bold text-uppercase">Total Preguntas</div>
                    <div className="h3 fw-bold mb-0 text-white">{faqCounts.total}</div>
                  </div>
                  <i className="mdi mdi-help-box-multiple" style={{ fontSize: '2.3rem', opacity: 0.85 }}></i>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={4}>
              <Card
                className="shadow-sm border-0 text-white"
                style={{
                  backgroundColor: '#059669',
                  borderRadius: cardBorderRadius,
                  cursor: 'pointer',
                  outline: statusFilter === '1' ? '3px solid #10b981' : 'none',
                  transform: statusFilter === '1' ? 'scale(1.02)' : 'none',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => handleChangeFaqStatusFilter('1')}
                title="Ver preguntas visibles en app móvil"
              >
                <Card.Body className="d-flex align-items-center justify-content-between p-3">
                  <div>
                    <div className="text-white-50 small fw-bold text-uppercase">Activas en App Móvil</div>
                    <div className="h3 fw-bold mb-0 text-white">{faqCounts.active}</div>
                  </div>
                  <i className="mdi mdi-cellphone-check" style={{ fontSize: '2.3rem', opacity: 0.85 }}></i>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={4}>
              <Card
                className="shadow-sm border-0 text-white"
                style={{
                  backgroundColor: '#d97706',
                  borderRadius: cardBorderRadius,
                  cursor: 'pointer',
                  outline: statusFilter === '0' ? '3px solid #f59e0b' : 'none',
                  transform: statusFilter === '0' ? 'scale(1.02)' : 'none',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => handleChangeFaqStatusFilter('0')}
                title="Ver preguntas ocultas / inactivas"
              >
                <Card.Body className="d-flex align-items-center justify-content-between p-3">
                  <div>
                    <div className="text-white-50 small fw-bold text-uppercase">Inactivas / Ocultas</div>
                    <div className="h3 fw-bold mb-0 text-white">{faqCounts.inactive}</div>
                  </div>
                  <i className="mdi mdi-eye-off-outline" style={{ fontSize: '2.3rem', opacity: 0.85 }}></i>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card
            className="shadow-sm mb-4"
            style={{
              borderRadius: cardBorderRadius,
              backgroundColor: containerBg,
              border: containerBorder,
            }}
          >
            <Card.Body className="p-3">
              <Row className="g-3 align-items-center">
                <Col xs={12} md={7}>
                  <Form.Group>
                    <Form.Label className="small fw-bold text-muted mb-1">
                      {t('general_search', 'Búsqueda General')}
                    </Form.Label>
                    <Form.Control
                      placeholder="Buscar por pregunta o contenido de la respuesta..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="customer-filter-control w-100 shadow-none"
                    />
                  </Form.Group>
                </Col>

                <Col xs={9} md={4}>
                  <Form.Group>
                    <Form.Label className="small fw-bold text-muted mb-1">
                      Estado en App Móvil
                    </Form.Label>
                    <Form.Select
                      value={statusFilter}
                      onChange={(e) => handleChangeFaqStatusFilter(e.target.value)}
                      className="customer-filter-control w-100 cursor-pointer shadow-none"
                    >
                      <option value="">Todos los Estados</option>
                      <option value="1">Solo Activas (Visibles en App)</option>
                      <option value="0">Solo Inactivas (Ocultas)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={3} md={1} className="d-flex align-items-end pt-md-3">
                  {(search || statusFilter !== '') && (
                    <Button
                      variant="link"
                      className="text-danger p-0 fw-bold small text-decoration-none"
                      onClick={() => { setSearch(''); handleChangeFaqStatusFilter(''); }}
                    >
                      <i className="mdi mdi-close-circle me-1"></i>
                      Limpiar
                    </Button>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <div
            className="card shadow-sm border-0"
            style={{
              borderRadius: cardBorderRadius,
              backgroundColor: containerBg,
              border: containerBorder,
            }}
          >
            <div className="card-body p-0">
              {loading ? (
                <div className="py-5">
                  <LoadingState
                    title="Cargando preguntas frecuentes..."
                    description="Consultando banco de preguntas y parámetros de publicación para la App Móvil"
                  />
                </div>
              ) : faqs.length === 0 ? (
                <div className="text-center py-5 px-3">
                  <i className="mdi mdi-help-circle-outline display-4 text-muted mb-3 d-block"></i>
                  <h5 className="fw-bold mb-2" style={{ color: titleColor }}>No se encontraron preguntas frecuentes</h5>
                  <p className="text-muted mb-3 small" style={{ maxWidth: '450px', margin: '0 auto' }}>
                    {search || statusFilter !== ''
                      ? 'No hay registros que coincidan con los criterios de búsqueda aplicados.'
                      : 'Aún no se han configurado preguntas frecuentes en el sistema.'}
                  </p>
                  <Button
                    style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '8px' }}
                    size="sm"
                    onClick={handleOpenCreate}
                  >
                    <i className="mdi mdi-plus me-1"></i> Crear la primera pregunta
                  </Button>
                </div>
              ) : (
                <div className="table-responsive" style={{ width: '100%', overflowX: 'auto' }}>
                  <Table responsive hover className="align-middle customer-table mb-0">
                    <thead style={{ backgroundColor: '#183eeb', color: '#ffffff' }}>
                      <tr style={{ backgroundColor: '#183eeb' }}>
                        <th style={{ backgroundColor: '#183eeb', color: '#ffffff', fontWeight: 'bold', textTransform: 'uppercase', padding: '15px 24px', fontSize: '0.82rem', border: 'none' }}>
                          Pregunta Frecuente
                        </th>
                        <th style={{ backgroundColor: '#183eeb', color: '#ffffff', fontWeight: 'bold', textTransform: 'uppercase', padding: '15px', fontSize: '0.82rem', border: 'none', width: '180px' }} className="text-center">
                          Estado en App
                        </th>
                        <th style={{ backgroundColor: '#183eeb', color: '#ffffff', fontWeight: 'bold', textTransform: 'uppercase', padding: '15px 24px', fontSize: '0.82rem', border: 'none', width: '140px' }} className="text-end">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {faqs.map((faq) => {
                        const iconMeta = getFaqIconMeta(faq.icono);
                        return (
                          <tr key={faq.id} className="customer-table-row">
                            <td className="px-4 py-3">
                              <div
                                className="d-flex align-items-center gap-3 cursor-pointer"
                                onClick={() => handleOpenDetails(faq)}
                                title="Clic para ver detalles completos y respuesta"
                              >
                                <div
                                  className="d-flex align-items-center justify-content-center shadow-xs flex-shrink-0"
                                  style={{
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '10px',
                                    backgroundColor: isDark ? '#1e293b' : '#eef2ff',
                                    color: '#183eeb',
                                    fontSize: '1.25rem',
                                  }}
                                >
                                  <i className={`mdi ${iconMeta.mdi}`}></i>
                                </div>
                                <div className="overflow-hidden">
                                  <div
                                    className="fw-bold text-truncate"
                                    style={{ color: titleColor, fontSize: '0.95rem' }}
                                  >
                                    {faq.pregunta}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="text-center py-3">
                              <button
                                type="button"
                                onClick={() => handleToggleStatus(faq)}
                                disabled={togglingId === faq.id}
                                className={`btn btn-sm ${faq.is_active ? 'btn-success' : 'btn-secondary'} rounded-pill px-3 py-1`}
                                style={{ fontSize: '0.8rem', fontWeight: '600', transition: 'all 0.2s ease', border: 'none' }}
                                title="Clic para cambiar visibilidad en la app móvil"
                              >
                                {togglingId === faq.id ? (
                                  <Spinner animation="border" size="sm" className="me-1" />
                                ) : (
                                  <i className={`mdi ${faq.is_active ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'} me-1`}></i>
                                )}
                                {faq.is_active ? 'Visible' : 'Oculto'}
                              </button>
                            </td>

                            <td className="px-4 py-3 text-end">
                              <div className="d-flex justify-content-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleOpenDetails(faq)}
                                  className="btn btn-sm btn-outline-info rounded-circle"
                                  style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  title="Ver Detalle y Respuesta"
                                >
                                  <i className="mdi mdi-eye"></i>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleOpenDelete(faq)}
                                  className="btn btn-sm btn-outline-danger rounded-circle"
                                  style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  title="Eliminar Pregunta"
                                >
                                  <i className="mdi mdi-delete"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </Tab>

        <Tab
          eventKey="tickets"
          title={
            <span>
              <i className="mdi mdi-message-text-clock-outline me-1"></i>
              Mensajes de Soporte (App Móvil)
              <Badge bg={ticketCounts.pendiente > 0 ? 'danger' : 'secondary'} pill className="ms-2">
                {ticketCounts.pendiente}
              </Badge>
            </span>
          }
        >
          <Row className="mb-4 g-3 mt-1">
            <Col xs={12} sm={6} lg={3}>
              <Card
                className="shadow-sm border-0 text-white"
                style={{
                  backgroundColor: isDark ? '#1e293b' : '#00024f',
                  borderRadius: cardBorderRadius,
                  cursor: 'pointer',
                  outline: ticketStatusFilter === '' ? '3px solid #183eeb' : 'none',
                  transform: ticketStatusFilter === '' ? 'scale(1.02)' : 'none',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => handleChangeTicketStatusFilter('')}
              >
                <Card.Body className="d-flex align-items-center justify-content-between p-3">
                  <div>
                    <div className="text-white-50 small fw-bold text-uppercase">Total Mensajes</div>
                    <div className="h3 fw-bold mb-0 text-white">{ticketCounts.total}</div>
                  </div>
                  <i className="mdi mdi-inbox-multiple-outline" style={{ fontSize: '2.2rem', opacity: 0.85 }}></i>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <Card
                className="shadow-sm border-0 text-white"
                style={{
                  backgroundColor: '#d97706',
                  borderRadius: cardBorderRadius,
                  cursor: 'pointer',
                  outline: ticketStatusFilter === 'pendiente' ? '3px solid #f59e0b' : 'none',
                  transform: ticketStatusFilter === 'pendiente' ? 'scale(1.02)' : 'none',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => handleChangeTicketStatusFilter('pendiente')}
              >
                <Card.Body className="d-flex align-items-center justify-content-between p-3">
                  <div>
                    <div className="text-white-50 small fw-bold text-uppercase">Pendientes</div>
                    <div className="h3 fw-bold mb-0 text-white">{ticketCounts.pendiente}</div>
                  </div>
                  <i className="mdi mdi-clock-alert-outline" style={{ fontSize: '2.2rem', opacity: 0.85 }}></i>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <Card
                className="shadow-sm border-0 text-white"
                style={{
                  backgroundColor: '#2563eb',
                  borderRadius: cardBorderRadius,
                  cursor: 'pointer',
                  outline: ticketStatusFilter === 'en_proceso' ? '3px solid #60a5fa' : 'none',
                  transform: ticketStatusFilter === 'en_proceso' ? 'scale(1.02)' : 'none',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => handleChangeTicketStatusFilter('en_proceso')}
              >
                <Card.Body className="d-flex align-items-center justify-content-between p-3">
                  <div>
                    <div className="text-white-50 small fw-bold text-uppercase">En Proceso</div>
                    <div className="h3 fw-bold mb-0 text-white">{ticketCounts.en_proceso}</div>
                  </div>
                  <i className="mdi mdi-progress-clock" style={{ fontSize: '2.2rem', opacity: 0.85 }}></i>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <Card
                className="shadow-sm border-0 text-white"
                style={{
                  backgroundColor: '#059669',
                  borderRadius: cardBorderRadius,
                  cursor: 'pointer',
                  outline: ticketStatusFilter === 'resuelto' ? '3px solid #10b981' : 'none',
                  transform: ticketStatusFilter === 'resuelto' ? 'scale(1.02)' : 'none',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => handleChangeTicketStatusFilter('resuelto')}
              >
                <Card.Body className="d-flex align-items-center justify-content-between p-3">
                  <div>
                    <div className="text-white-50 small fw-bold text-uppercase">Resueltos</div>
                    <div className="h3 fw-bold mb-0 text-white">{ticketCounts.resuelto}</div>
                  </div>
                  <i className="mdi mdi-check-decagram-outline" style={{ fontSize: '2.2rem', opacity: 0.85 }}></i>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card
            className="shadow-sm mb-4"
            style={{
              borderRadius: cardBorderRadius,
              backgroundColor: containerBg,
              border: containerBorder,
            }}
          >
            <Card.Body className="p-3">
              <Row className="g-3 align-items-center">
                <Col xs={12} md={7}>
                  <Form.Group>
                    <Form.Label className="small fw-bold text-muted mb-1">
                      Búsqueda de Tickets
                    </Form.Label>
                    <Form.Control
                      placeholder="Buscar por código SUP-..., cliente, RIF, usuario, correo o mensaje..."
                      value={ticketSearch}
                      onChange={(e) => setTicketSearch(e.target.value)}
                      className="customer-filter-control w-100 shadow-none"
                    />
                  </Form.Group>
                </Col>

                <Col xs={9} md={4}>
                  <Form.Group>
                    <Form.Label className="small fw-bold text-muted mb-1">
                      Estado del Ticket
                    </Form.Label>
                    <Form.Select
                      value={ticketStatusFilter}
                      onChange={(e) => handleChangeTicketStatusFilter(e.target.value)}
                      className="customer-filter-control w-100 cursor-pointer shadow-none"
                    >
                      <option value="">Todos los Estados</option>
                      <option value="pendiente">Pendientes por atender</option>
                      <option value="en_proceso">En Proceso</option>
                      <option value="resuelto">Resueltos</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={3} md={1} className="d-flex align-items-end pt-md-3">
                  {(ticketSearch || ticketStatusFilter !== '') && (
                    <Button
                      variant="link"
                      className="text-danger p-0 fw-bold small text-decoration-none"
                      onClick={() => { setTicketSearch(''); handleChangeTicketStatusFilter(''); }}
                    >
                      <i className="mdi mdi-close-circle me-1"></i>
                      Limpiar
                    </Button>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <div
            className="card shadow-sm border-0"
            style={{
              borderRadius: cardBorderRadius,
              backgroundColor: containerBg,
              border: containerBorder,
            }}
          >
            <div className="card-body p-0">
              {loadingTickets ? (
                <div className="py-5">
                  <LoadingState
                    title="Cargando bandeja de soporte..."
                    description="Consultando mensajes enviados por clientes y usuarios desde la App Móvil"
                  />
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-5 px-3">
                  <i className="mdi mdi-message-check-outline display-4 text-muted mb-3 d-block"></i>
                  <h5 className="fw-bold mb-2" style={{ color: titleColor }}>No hay mensajes de soporte registrados</h5>
                  <p className="text-muted mb-0 small" style={{ maxWidth: '480px', margin: '0 auto' }}>
                    {ticketSearch || ticketStatusFilter !== ''
                      ? 'No se encontraron tickets de soporte con los filtros seleccionados.'
                      : 'Cuando un usuario o cliente envíe una consulta desde el formulario de Soporte en la App Móvil, aparecerá aquí inmediatamente.'}
                  </p>
                </div>
              ) : (
                <div className="table-responsive" style={{ width: '100%', overflowX: 'auto' }}>
                  <Table responsive hover className="align-middle customer-table mb-0">
                    <thead style={{ backgroundColor: '#183eeb', color: '#ffffff' }}>
                      <tr style={{ backgroundColor: '#183eeb' }}>
                        <th style={{ backgroundColor: '#183eeb', color: '#ffffff', fontWeight: 'bold', textTransform: 'uppercase', padding: '15px 20px', fontSize: '0.8rem', border: 'none' }}>
                          Ticket / Fecha
                        </th>
                        <th style={{ backgroundColor: '#183eeb', color: '#ffffff', fontWeight: 'bold', textTransform: 'uppercase', padding: '15px', fontSize: '0.8rem', border: 'none' }}>
                          Remitente / Contacto
                        </th>
                        <th style={{ backgroundColor: '#183eeb', color: '#ffffff', fontWeight: 'bold', textTransform: 'uppercase', padding: '15px', fontSize: '0.8rem', border: 'none' }}>
                          Cliente / Rol
                        </th>
                        <th style={{ backgroundColor: '#183eeb', color: '#ffffff', fontWeight: 'bold', textTransform: 'uppercase', padding: '15px', fontSize: '0.8rem', border: 'none', maxWidth: '340px' }}>
                          Mensaje Recibido
                        </th>
                        <th style={{ backgroundColor: '#183eeb', color: '#ffffff', fontWeight: 'bold', textTransform: 'uppercase', padding: '15px', fontSize: '0.8rem', border: 'none', width: '175px' }} className="text-center">
                          Estado
                        </th>
                        <th style={{ backgroundColor: '#183eeb', color: '#ffffff', fontWeight: 'bold', textTransform: 'uppercase', padding: '15px 20px', fontSize: '0.8rem', border: 'none' }} className="text-end">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((ticket) => {
                        const statusMeta = getTicketStatusMeta(ticket.estatus);
                        const isUpdatingThis = updatingTicketId === ticket.id;
                        return (
                          <tr key={ticket.id} className="customer-table-row">
                            <td className="px-4 py-3">
                              <div
                                className="fw-bold cursor-pointer"
                                style={{ color: '#183eeb', fontSize: '0.9rem' }}
                                onClick={() => handleOpenTicketModal(ticket)}
                              >
                                {ticket.ticket_code}
                              </div>
                              <div className="text-muted small">
                                {ticket.created_at ? new Date(ticket.created_at).toLocaleString('es-VE', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                }) : '—'}
                              </div>
                            </td>

                            <td className="py-3">
                              <div className="fw-bold" style={{ color: titleColor, fontSize: '0.9rem' }}>
                                {ticket.user_name || 'Usuario App'}
                              </div>
                              {ticket.user_email && (
                                <div className="text-muted small">{ticket.user_email}</div>
                              )}
                              {ticket.user_phone && (
                                <div className="small text-success fw-semibold">
                                  <i className="mdi mdi-phone me-1"></i>
                                  {ticket.user_phone}
                                </div>
                              )}
                            </td>

                            <td className="py-3">
                              {ticket.razon_social ? (
                                <>
                                  <div className="fw-semibold" style={{ color: titleColor, fontSize: '0.88rem' }}>
                                    {ticket.razon_social}
                                  </div>
                                  <div className="text-muted small">{ticket.rif_cedula || ''}</div>
                                </>
                              ) : (
                                <span className="badge bg-light text-dark border">
                                  {ticket.user_role || 'App Móvil'}
                                </span>
                              )}
                            </td>

                            <td className="py-3" style={{ maxWidth: '340px' }}>
                              <div
                                className="text-truncate cursor-pointer"
                                style={{ color: titleColor, fontSize: '0.9rem' }}
                                onClick={() => handleOpenTicketModal(ticket)}
                                title={ticket.mensaje}
                              >
                                {ticket.mensaje}
                              </div>
                              {ticket.notas_admin && (
                                <div className="text-muted small text-truncate mt-1">
                                  <i className="mdi mdi-note-text-outline me-1 text-primary"></i>
                                  Nota: {ticket.notas_admin}
                                </div>
                              )}
                            </td>

                            <td className="text-center py-3">
                              <div className="d-inline-flex align-items-center justify-content-center position-relative">
                                {isUpdatingThis ? (
                                  <span
                                    className="badge rounded-pill px-3 py-1"
                                    style={{ backgroundColor: statusMeta.bg, color: statusMeta.color, fontSize: '0.78rem' }}
                                  >
                                    <Spinner animation="border" size="sm" className="me-1" />
                                    Guardando...
                                  </span>
                                ) : (
                                  <select
                                    value={ticket.estatus || 'pendiente'}
                                    onChange={(e) => handleQuickChangeTicketStatus(ticket, e.target.value)}
                                    aria-label={`Cambiar estado del ticket ${ticket.ticket_code}`}
                                    title="Clic para cambiar y guardar el estado del ticket"
                                    style={{
                                      backgroundColor: statusMeta.bg,
                                      color: statusMeta.color,
                                      border: `1px solid ${statusMeta.border}`,
                                      borderRadius: '999px',
                                      padding: '4px 12px',
                                      fontWeight: 600,
                                      fontSize: '0.78rem',
                                      cursor: 'pointer',
                                      outline: 'none',
                                      textAlign: 'center',
                                    }}
                                  >
                                    <option value="pendiente" style={{ backgroundColor: '#ffffff', color: '#0f172a' }}>
                                      Pendiente
                                    </option>
                                    <option value="en_proceso" style={{ backgroundColor: '#ffffff', color: '#0f172a' }}>
                                      En Proceso
                                    </option>
                                    <option value="resuelto" style={{ backgroundColor: '#ffffff', color: '#0f172a' }}>
                                      Resuelto
                                    </option>
                                  </select>
                                )}
                              </div>
                            </td>

                            <td className="px-4 py-3 text-end">
                              <div className="d-flex justify-content-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleOpenTicketModal(ticket)}
                                  className="btn btn-sm btn-outline-primary rounded-circle"
                                  style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  title="Ver y Gestionar Ticket"
                                >
                                  <i className="mdi mdi-eye"></i>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleOpenDeleteTicket(ticket)}
                                  className="btn btn-sm btn-outline-danger rounded-circle"
                                  style={{ width: '34px', height: '34px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  title="Eliminar Ticket"
                                >
                                  <i className="mdi mdi-delete"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </Tab>
      </Tabs>

      {/* MODAL DETALLES DE LA PREGUNTA */}
      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5 mb-0 fw-bold d-flex align-items-center gap-2">
            <i className="mdi mdi-information-outline text-primary"></i>
            Detalle de la Pregunta Frecuente
          </Modal.Title>
        </Modal.Header>
        {selectedFaq && (() => {
          const iconMeta = getFaqIconMeta(selectedFaq.icono);
          return (
            <Modal.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-3 mb-3 border-bottom">
                <div className="small fw-semibold text-muted d-flex align-items-center gap-1">
                  <i className="mdi mdi-cellphone-check text-primary"></i>
                  Sincronizado con App Móvil
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => handleToggleStatus(selectedFaq)}
                    disabled={togglingId === selectedFaq.id}
                    className={`btn btn-sm ${selectedFaq.is_active ? 'btn-success' : 'btn-secondary'} rounded-pill px-3 py-1`}
                    style={{ fontSize: '0.8rem', fontWeight: '600', border: 'none' }}
                    title="Clic para cambiar visibilidad"
                  >
                    <i className={`mdi ${selectedFaq.is_active ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'} me-1`}></i>
                    {selectedFaq.is_active ? 'Visible en App Móvil' : 'Oculto en App Móvil'}
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="small fw-bold text-muted text-uppercase mb-2 d-block">
                  Pregunta Formulada
                </label>
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0 shadow-xs"
                    style={{
                      width: '46px',
                      height: '46px',
                      borderRadius: '12px',
                      backgroundColor: isDark ? '#1e293b' : '#eef2ff',
                      color: '#183eeb',
                      fontSize: '1.6rem',
                    }}
                  >
                    <i className={`mdi ${iconMeta.mdi}`}></i>
                  </div>
                  <div>
                    <h5 className="fw-bold mb-1" style={{ color: titleColor, lineHeight: '1.4' }}>
                      {selectedFaq.pregunta}
                    </h5>
                    <div className="small text-muted">
                      {iconMeta.label} &bull; {iconMeta.category}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="small fw-bold text-muted text-uppercase mb-1 d-block">
                  Respuesta Detallada (Visible para el Cliente en la App)
                </label>
                <div
                  className="p-3 rounded shadow-xs"
                  style={{
                    backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                    border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                    borderLeft: '4px solid #183eeb',
                    lineHeight: '1.65',
                    whiteSpace: 'pre-wrap',
                    color: isDark ? '#f8fafc' : '#334155',
                    fontSize: '0.92rem',
                  }}
                >
                  {selectedFaq.respuesta}
                </div>
              </div>
            </Modal.Body>
          );
        })()}
        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant="secondary"
            onClick={() => setShowDetailsModal(false)}
            style={{ borderRadius: '8px' }}
          >
            Cerrar
          </Button>
          {selectedFaq && (
            <Button
              style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '8px', fontWeight: 'bold' }}
              onClick={() => handleTransitionToEdit(selectedFaq)}
              className="d-flex align-items-center gap-1"
            >
              <i className="mdi mdi-pencil me-1"></i>
              Editar Pregunta
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* MODAL CREAR / EDITAR PREGUNTA */}
      <Modal
        show={showFormModal}
        onHide={() => !saving && setShowFormModal(false)}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title className="h5 mb-0 d-flex align-items-center gap-2 fw-bold">
            <i className={`mdi ${modalMode === 'create' ? 'mdi-plus-circle' : 'mdi-pencil'} text-primary`}></i>
            {modalMode === 'create' ? 'Nueva Pregunta Frecuente' : 'Editar Pregunta Frecuente'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveFaq}>
          <Modal.Body className="p-4">
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold mb-1">
                    Pregunta <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: ¿Cuáles son los tiempos estimados de entrega?"
                    value={formData.pregunta}
                    onChange={(e) => setFormData({ ...formData, pregunta: e.target.value })}
                    isInvalid={Boolean(formErrors.pregunta)}
                    className="customer-filter-control w-100 shadow-none"
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.pregunta}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col xs={12} md={7}>
                <Form.Group>
                  <Form.Label className="small fw-bold mb-1">
                    Ícono de la Pregunta
                  </Form.Label>
                  <FaqIconPicker
                    value={formData.icono}
                    onChange={(key) => setFormData({ ...formData, icono: key })}
                    isDark={isDark}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={5}>
                <Form.Group>
                  <Form.Label className="small fw-bold mb-1">Estado en App Móvil</Form.Label>
                  <div>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                      className={`btn w-100 ${formData.is_active ? 'btn-success' : 'btn-secondary'} rounded-pill`}
                      style={{ height: '44px', fontWeight: '600', fontSize: '0.85rem', border: 'none' }}
                    >
                      <i className={`mdi ${formData.is_active ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'} me-1`}></i>
                      {formData.is_active ? 'Visible en App' : 'Oculto en App'}
                    </button>
                  </div>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold mb-1">
                    Respuesta Detallada <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    placeholder="Escribe la respuesta detallada y clara que leerá el usuario en la app..."
                    value={formData.respuesta}
                    onChange={(e) => setFormData({ ...formData, respuesta: e.target.value })}
                    isInvalid={Boolean(formErrors.respuesta)}
                    style={{
                      borderRadius: '8px',
                      border: isDark ? '1.5px solid #dcdfe6' : '1.5px solid #183eeb',
                      backgroundColor: isDark ? 'transparent' : '#ffffff',
                      color: isDark ? '#ffffff' : '#010028',
                      padding: '12px 14px',
                    }}
                  />
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <Form.Control.Feedback type="invalid">
                      {formErrors.respuesta}
                    </Form.Control.Feedback>
                    <span className="text-muted ms-auto small">
                      {formData.respuesta.length} caracteres
                    </span>
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowFormModal(false)}
              disabled={saving}
              style={{ borderRadius: '8px' }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="d-flex align-items-center gap-1"
              style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '8px', fontWeight: 'bold' }}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span>Guardando...</span>
                </>
              ) : (
                <>
                  <i className="mdi mdi-content-save-outline me-1"></i>
                  <span>{modalMode === 'create' ? 'Crear Pregunta' : 'Guardar Cambios'}</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL CONFIRMAR ELIMINACIÓN DE FAQ */}
      <Modal
        show={showDeleteModal}
        onHide={() => !deleting && setShowDeleteModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!deleting}>
          <Modal.Title className="h5 mb-0 d-flex align-items-center gap-2 fw-bold text-danger">
            <i className="mdi mdi-alert-circle text-danger"></i>
            Confirmar Eliminación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="mb-2">¿Estás seguro de que deseas eliminar permanentemente esta pregunta frecuente?</p>
          {faqToDelete && (
            <div
              className="p-3 rounded border"
              style={{
                backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                borderColor: isDark ? '#334155' : '#e2e8f0',
              }}
            >
              <p className="fw-bold mb-0" style={{ color: titleColor }}>{faqToDelete.pregunta}</p>
            </div>
          )}
          <p className="text-danger small mt-3 mb-0">
            <i className="mdi mdi-alert-outline me-1"></i>
            Esta acción no se puede deshacer. La pregunta dejará de aparecer inmediatamente en la aplicación móvil.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
            style={{ borderRadius: '8px' }}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={deleting}
            className="d-flex align-items-center gap-1"
            style={{ borderRadius: '8px', fontWeight: 'bold' }}
          >
            {deleting ? (
              <>
                <Spinner animation="border" size="sm" />
                <span>Eliminando...</span>
              </>
            ) : (
              <>
                <i className="mdi mdi-trash-can-outline me-1"></i>
                <span>Eliminar Definitivamente</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL DETALLE Y GESTIÓN DE TICKET DE SOPORTE */}
      <Modal
        show={showTicketModal}
        onHide={() => !savingTicket && setShowTicketModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton={!savingTicket}>
          <Modal.Title className="h5 mb-0 fw-bold d-flex align-items-center gap-2">
            <i className="mdi mdi-lifebuoy text-primary"></i>
            Solicitud de Soporte {selectedTicket ? `— ${selectedTicket.ticket_code}` : ''}
          </Modal.Title>
        </Modal.Header>
        {selectedTicket && (
          <Form onSubmit={handleSaveTicket}>
            <Modal.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-3 mb-3 border-bottom">
                <div className="small text-muted">
                  <i className="mdi mdi-calendar-clock me-1"></i>
                  Recibido el{' '}
                  <strong>
                    {selectedTicket.created_at
                      ? new Date(selectedTicket.created_at).toLocaleString('es-VE')
                      : '—'}
                  </strong>
                  {selectedTicket.attended_at && (
                    <span className="ms-2">
                      &bull; Atendido el{' '}
                      <strong>
                        {new Date(selectedTicket.attended_at).toLocaleString('es-VE')}
                      </strong>
                      {selectedTicket.attendant?.nombre_completo ? ` por ${selectedTicket.attendant.nombre_completo}` : ''}
                    </span>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  {renderTicketStatusBadge(ticketEditForm.estatus)}
                </div>
              </div>

              <Row className="g-3 mb-4">
                <Col xs={12} md={6}>
                  <div
                    className="p-3 rounded h-100"
                    style={{
                      backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                      border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                    }}
                  >
                    <div className="small fw-bold text-muted text-uppercase mb-2">
                      Datos del Remitente
                    </div>
                    <div className="fw-bold mb-1" style={{ color: titleColor }}>
                      {selectedTicket.user_name || 'Usuario App Móvil'}
                    </div>
                    <div className="small text-muted mb-1">
                      <i className="mdi mdi-email-outline me-1"></i>
                      {selectedTicket.user_email || 'Sin correo'}
                    </div>
                    <div className="small text-muted">
                      <i className="mdi mdi-phone-outline me-1"></i>
                      {selectedTicket.user_phone || 'Sin teléfono registrado'}
                    </div>
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div
                    className="p-3 rounded h-100"
                    style={{
                      backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                      border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                    }}
                  >
                    <div className="small fw-bold text-muted text-uppercase mb-2">
                      Cliente / Perfil Asociado
                    </div>
                    {selectedTicket.razon_social ? (
                      <>
                        <div className="fw-bold mb-1" style={{ color: titleColor }}>
                          {selectedTicket.razon_social}
                        </div>
                        <div className="small text-muted mb-1">
                          RIF / Cédula: <strong>{selectedTicket.rif_cedula || 'N/A'}</strong>
                        </div>
                      </>
                    ) : (
                      <div className="small text-muted mb-1">Sin comercio asociado</div>
                    )}
                    <div className="small text-muted">
                      Rol: <Badge bg="secondary">{selectedTicket.user_role || 'App'}</Badge>
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="mb-4">
                <label className="small fw-bold text-muted text-uppercase mb-2 d-block">
                  Mensaje Enviado desde la App Móvil
                </label>
                <div
                  className="p-3 rounded"
                  style={{
                    backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                    border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                    borderLeft: '4px solid #183eeb',
                    whiteSpace: 'pre-wrap',
                    color: titleColor,
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                  }}
                >
                  {selectedTicket.mensaje}
                </div>
              </div>

              <Row className="g-3">
                <Col xs={12} md={5}>
                  <Form.Group>
                    <Form.Label className="small fw-bold mb-1">
                      Estado de Atención
                    </Form.Label>
                    <Form.Select
                      value={ticketEditForm.estatus}
                      onChange={(e) => setTicketEditForm({ ...ticketEditForm, estatus: e.target.value })}
                      className="customer-filter-control w-100 shadow-none"
                    >
                      <option value="pendiente">Pendiente</option>
                      <option value="en_proceso">En Proceso</option>
                      <option value="resuelto">Resuelto</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} md={7}>
                  <Form.Group>
                    <Form.Label className="small fw-bold mb-1">
                      Notas Internas de Seguimiento (Backoffice)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Registra aquí las observaciones o la solución brindada al usuario..."
                      value={ticketEditForm.notas_admin}
                      onChange={(e) => setTicketEditForm({ ...ticketEditForm, notas_admin: e.target.value })}
                      style={{
                        borderRadius: '8px',
                        backgroundColor: isDark ? 'transparent' : '#ffffff',
                        color: titleColor,
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
              <Button
                variant="secondary"
                onClick={() => setShowTicketModal(false)}
                disabled={savingTicket}
                style={{ borderRadius: '8px' }}
              >
                Cerrar
              </Button>
              <Button
                type="submit"
                disabled={savingTicket}
                style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '8px', fontWeight: 'bold' }}
              >
                {savingTicket ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-content-save-outline me-1"></i>
                    Guardar Gestión
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* MODAL CONFIRMAR ELIMINACIÓN DE TICKET */}
      <Modal
        show={showDeleteTicketModal}
        onHide={() => !deletingTicket && setShowDeleteTicketModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!deletingTicket}>
          <Modal.Title className="h5 mb-0 d-flex align-items-center gap-2 fw-bold text-danger">
            <i className="mdi mdi-alert-circle text-danger"></i>
            Eliminar Ticket de Soporte
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="mb-2">¿Estás seguro de que deseas eliminar este mensaje de soporte?</p>
          {ticketToDelete && (
            <div
              className="p-3 rounded border"
              style={{
                backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                borderColor: isDark ? '#334155' : '#e2e8f0',
              }}
            >
              <div className="fw-bold text-primary mb-1">{ticketToDelete.ticket_code} — {ticketToDelete.user_name}</div>
              <div className="small text-muted text-truncate">{ticketToDelete.mensaje}</div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteTicketModal(false)}
            disabled={deletingTicket}
            style={{ borderRadius: '8px' }}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDeleteTicket}
            disabled={deletingTicket}
            style={{ borderRadius: '8px', fontWeight: 'bold' }}
          >
            {deletingTicket ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Eliminando...
              </>
            ) : (
              <>
                <i className="mdi mdi-trash-can-outline me-1"></i>
                Eliminar Ticket
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FaqManager;
